import { SelectOption } from '@ardoq/select';
import {
  APIReportFilters,
  ArdoqId,
  columnPredicates,
  DataSourceType,
  FilterExpression,
  FilterValue,
  isNumberInterval,
  NUMBER_FORMAT_PERCENTAGE,
  Operator,
  ReportColumn,
  ReportColumnType,
} from '@ardoq/api-types';
import {
  percentageStoredValueToEditValue,
  formatNumber,
  Locale,
} from '@ardoq/locale';
import {
  CheckboxColumnFilterProps,
  ColumnFilterMultiselectWithLoadSuggestionsProps,
  FilterInputProps,
  FilterInputType,
  FilterWithOperatorProps,
  ReportColumnSuggestionsFetchFn,
  ReportFilterInputCommands,
  TextColumnFilterProps,
  UnknownColumnFilterInputProps,
  ValuePresenceColumnFilterProps,
} from '../types';
import { ListPopoverOptionProps } from '@ardoq/table-filter-bar';
import { presentationApi, reportApi } from '@ardoq/api';
import { isTruthy, isArdoqError, isJust, Maybe } from '@ardoq/common-helpers';
import {
  isBetweenOptionSelected,
  isEmptyOptionSelected,
  isColumnWithCustomLabel,
  isUserColumn,
  isReferenceColumn,
  splitDateRangeColumnForUI,
} from '../utils';
import {
  isEmpty,
  isNull,
  isNumber,
  isObject,
  isString,
  isUndefined,
  pickBy,
} from 'lodash';
import { IconName } from '@ardoq/icons';
import {
  isDateRangeReversed,
  isISO8601DateOnly,
  relativeDateDisplayLabel,
} from '@ardoq/date-time';
import { ActiveBarFilters } from '@ardoq/snowflakes';
import { isRelativeDate } from '@ardoq/date-time-picker';
import { type I18nLocale } from '@ardoq/i18n';

// The current report filters don't distinguish between not set and N/A values,
// drilldown api is returning both togther when filter by null value.
// Dashboard api has the same behavior.
// This label is used at both cases to represent null value correctly.
export const UNSET_OR_NOTAPPLICABLE_VALUE_LABEL = 'Not set or N/A';

// This value is only for frontend displaying purpose, should not be used in API calls.
// Currently used by checkbox or reference columns filters for is-not-empty operator,
// when there is no spearate operator selectors
export const HAS_VALUE_OR_REFERENCE_VALUE = '__ardoq-has-any-value__';

const NO_REFERENCE_LABEL = 'No reference';
const HAS_REFERENCE_LABEL = 'Has reference';
export const HAS_VALUE_LABEL = 'Has value';

const CheckboxDisplayValueToActualValue = (v: FilterValue): FilterValue => {
  if (v === 'true') return true;
  if (v === 'false') return false;
  return v;
};

const CheckboxActualValueToDisplayValue = (v: FilterValue): FilterValue => {
  if (v === true) return 'true';
  if (v === false) return 'false';
  return v;
};

const checkboxLabelMap: Record<string, Maybe<string>> = {
  Checked: 'true',
  Unchecked: 'false',
  [HAS_VALUE_LABEL]: HAS_VALUE_OR_REFERENCE_VALUE,
  [UNSET_OR_NOTAPPLICABLE_VALUE_LABEL]: null,
};

export const VALUE_PRESENCE_FILTER_OPERATORS = [
  Operator.IS_NOT_EMPTY,
  Operator.IS_EMPTY,
];

const checkboxFilterLabels = Object.keys(checkboxLabelMap) as Array<string>;

const getCheckboxColumnFilterOptions = (
  column: ReportColumn
): SelectOption<FilterValue>[] => {
  if (!columnPredicates.isCheckBoxColumn(column)) {
    throw new Error('only checkbox supported!');
  }

  return checkboxFilterLabels.map(value => {
    const actualValue = CheckboxDisplayValueToActualValue(
      checkboxLabelMap[value as keyof typeof checkboxLabelMap]
    );

    return {
      label: value,
      value: CheckboxActualValueToDisplayValue(actualValue),
    };
  });
};

export const getLoadReportColumnSuggestionsAsync = (
  reportId: ArdoqId
): ReportColumnSuggestionsFetchFn => {
  return async (columnKey: string, query: string) => {
    const results = await reportApi.getColumnSuggestions(
      reportId,
      columnKey,
      query
    );

    if (isArdoqError(results)) {
      return [];
    }
    return results;
  };
};

export const getLoadPresentationReportColumnSuggestionsAsync = (
  reportId: ArdoqId,
  slideId: ArdoqId,
  presentationId: ArdoqId
): ReportColumnSuggestionsFetchFn => {
  return async (columnKey: string, query: string) => {
    const results = await presentationApi.fetchReportColumnSuggestions(
      presentationId,
      reportId,
      slideId,
      columnKey,
      query
    );

    if (isArdoqError(results)) {
      return [];
    }
    return results;
  };
};

const ValidGenericFilterOperators: Operator[] = [
  Operator.EQUAL,
  Operator.IS_EMPTY,
  Operator.IS_NOT_EMPTY,
] as const;

export const isFilterExpressionValid = ({
  value,
  operator,
}: FilterExpression) =>
  !isUndefined(value) &&
  operator &&
  value !== HAS_VALUE_OR_REFERENCE_VALUE &&
  ValidGenericFilterOperators.includes(operator);

export const TEXT_FILTER_OPERATORS: Operator[] = [
  Operator.EQUAL,
  Operator.CONTAINS_SUBSTRING,
  Operator.IS_EMPTY,
  Operator.IS_NOT_EMPTY,
];
const isTextFilterExpressionValid = ({ value, operator }: FilterExpression) => {
  if (isUndefined(operator)) return false;
  if ([Operator.IS_EMPTY, Operator.IS_NOT_EMPTY].includes(operator))
    return isNull(value);

  return !isNull(value) && TEXT_FILTER_OPERATORS.includes(operator);
};

export const isNumberFilterExpressionValid = ({
  value,
  operator,
}: FilterExpression) => {
  if (isUndefined(operator)) return false;
  if ([Operator.IS_EMPTY, Operator.IS_NOT_EMPTY].includes(operator))
    return isNull(value);

  // Need to check null & NaN because number input may initialize as null!
  if (isBetweenOptionSelected(operator))
    return (
      isNumberInterval(value) &&
      !isNull(value.start) &&
      !isNull(value.end) &&
      !isNaN(value.start) &&
      !isNaN(value.end) &&
      value.start <= value.end
    );

  return (
    isJust(value) &&
    !isNumberInterval(value) &&
    isNumber(value) &&
    !isNaN(value)
  );
};

export const isDateString = (value: FilterValue) =>
  isJust(value) &&
  isString(value) &&
  (isISO8601DateOnly(value) || isRelativeDate(value));

export const isDateStringInterval = (
  value: FilterValue
): value is {
  start: string | null;
  end: string | null;
} =>
  isObject(value) &&
  'start' in value &&
  'end' in value &&
  (isDateString(value.start) || isNull(value.start)) &&
  (isDateString(value.end) || isNull(value.end));

export const isDateFilterExpressionValid = ({
  value,
  operator,
}: FilterExpression) => {
  if (isUndefined(operator)) return false;

  if ([Operator.IS_EMPTY, Operator.IS_NOT_EMPTY].includes(operator))
    return true;

  if (isBetweenOptionSelected(operator)) {
    return (
      isDateStringInterval(value) &&
      isDateString(value.start) &&
      isDateString(value.end) &&
      !isDateRangeReversed({ start: value.start, end: value.end })
    );
  }

  return isDateString(value);
};

const isFilterValid = (
  expressions: FilterExpression[],
  column: ReportColumn | undefined
) => {
  if (!column || isEmpty(expressions)) return false;

  if (columnPredicates.isNumberColumn(column)) {
    return expressions.every(isNumberFilterExpressionValid);
  }

  if (columnPredicates.isDateColumn(column)) {
    return expressions.every(isDateFilterExpressionValid);
  }

  if (
    columnPredicates.isTextColumn(column) ||
    columnPredicates.isEmailColumn(column)
  ) {
    return expressions.every(isTextFilterExpressionValid);
  }

  return expressions.every(isFilterExpressionValid);
};

const hasIncompleteFilterValue = (v: FilterExpression): boolean => {
  if (v.value === null && !isEmptyOptionSelected(v.operator)) {
    return true;
  }
  if (
    isBetweenOptionSelected(v.operator) &&
    (isNumberInterval(v.value) || isDateStringInterval(v.value)) &&
    (v.value.start === null ||
      v.value.end === null ||
      // the between filter with start value higher than end value is also considered as incomplete filter,
      // as it's not valid and can't be applied/preserved until user fix it.
      (isNumberInterval(v.value) && v.value.start > v.value.end) ||
      (isDateStringInterval(v.value) &&
        isDateRangeReversed({ start: v.value.start, end: v.value.end })))
  ) {
    return true;
  }
  return false;
};

// indicate newly added WIP filters which might have incomplete filter expressions
export const isFilterIncomplete = (expressions: FilterExpression[]): boolean =>
  isEmpty(expressions) || expressions.some(v => hasIncompleteFilterValue(v));

export const getReferenceDirectionIcon = (
  referenceType: ReportColumnType
): IconName =>
  referenceType === ReportColumnType.REFERENCE_TYPE_INCOMING
    ? IconName.ARROW_BACK
    : IconName.ARROW_FORWARD;

export const getAddFilterButtonOptions = ({
  filterableColumns,
  filters,
  dataSource,
  removeFilterExpression,
  addEmptyFilterExpression,
}: {
  filterableColumns: ReportColumn[];
  filters: APIReportFilters | null;
  dataSource?: DataSourceType;
  removeFilterExpression: (columnKey: ArdoqId) => void;
  addEmptyFilterExpression: (column: ReportColumn) => void;
}): ListPopoverOptionProps[] => {
  const isGremlinReport = dataSource === DataSourceType.GREMLIN_SEARCH;
  return filterableColumns
    .filter(column => !(isGremlinReport && isReferenceColumn(column)))
    .flatMap(splitDateRangeColumnForUI)
    .map(column => {
      const isSelected = filters && column.key in filters;
      const rightIconName =
        isReferenceColumn(column) && !isColumnWithCustomLabel(column)
          ? getReferenceDirectionIcon(column.type)
          : null;

      const onClick = () => {
        if (isSelected) return removeFilterExpression(column.key);
        return addEmptyFilterExpression(column);
      };

      return {
        id: column.key,
        label: column.label,
        isChecked: Boolean(isSelected),
        icon: null,
        count: null,
        onCheckboxClick: onClick,
        onClick: onClick,
        rightIcon: rightIconName ? { name: rightIconName } : null,
      };
    });
};

const getSelectedValueForFieldWithOperator = (
  filters: APIReportFilters,
  column: ReportColumn
): FilterExpression =>
  filters[column.key]?.values[0] ?? {
    value: null,
  };

/**
 * Converts stored filter values to display values for editing.
 * For percentage fields, converts stored fractions (0.5) to percentages (50).
 * For non-percentage fields, returns undefined (uses stored value directly).
 */
export const convertStoredValueToDisplayValue = (
  value: FilterValue,
  isPercentage: boolean
): FilterValue | undefined => {
  if (!isPercentage) {
    return undefined;
  }

  // Check if value looks like an interval (has start and end properties)
  // Handle partial intervals where start or end might be undefined/null
  if (isNumberInterval(value)) {
    const start = value.start;
    const end = value.end;
    return {
      start: isNumber(start)
        ? percentageStoredValueToEditValue(start)
        : (start as any),
      end: isNumber(end) ? percentageStoredValueToEditValue(end) : (end as any),
    };
  }

  return isNumber(value) ? percentageStoredValueToEditValue(value) : value;
};

/**
 * Formats stored filter values for presentation (when input is not focused).
 * For percentage fields, formats stored fractions as percentage strings (0.5 → "50%").
 * For intervals, always returns an object structure { start, end }.
 * For single values, returns formatted string (if percentage) or undefined.
 */
export const formatDisplayValueForPresentation = (
  value: FilterValue,
  isPercentage: boolean,
  locale: Locale
): string | { start?: string; end?: string } | undefined => {
  // Check if value looks like an interval (has start and end properties)
  // Handle partial intervals where start or end might be undefined
  if (isNumberInterval(value)) {
    return {
      start:
        isPercentage && isNumber(value.start)
          ? formatNumber(value.start, 'percentage', locale)
          : undefined,
      end:
        isPercentage && isNumber(value.end)
          ? formatNumber(value.end, 'percentage', locale)
          : undefined,
    };
  }

  return isPercentage && isNumber(value)
    ? formatNumber(value, 'percentage', locale)
    : undefined;
};

const getFilterWithOperatorProps = (
  column: ReportColumn,
  filters: APIReportFilters,
  locale: Locale,
  removeFilterExpression: ReportFilterInputCommands['removeFilterExpression'],
  setFilterExpression: ReportFilterInputCommands['setFilterExpression'],
  excludedOperators?: Operator[]
): FilterWithOperatorProps => {
  const expression = getSelectedValueForFieldWithOperator(filters, column);
  const isPercentage = column.numberFormatOptions === NUMBER_FORMAT_PERCENTAGE;

  const displayValue = convertStoredValueToDisplayValue(
    expression.value,
    isPercentage
  );

  const formattedDisplayValue = formatDisplayValueForPresentation(
    expression.value,
    isPercentage,
    locale
  );

  return {
    column,
    expression: {
      ...expression,
      displayValue,
      formattedDisplayValue,
    },
    excludedOperators,
    removeFilter: removeFilterExpression,
    changeFilterExpression: setFilterExpression,
  };
};

export const getNoValueFilterLabel = (column: ReportColumn): string =>
  isReferenceColumn(column)
    ? NO_REFERENCE_LABEL
    : UNSET_OR_NOTAPPLICABLE_VALUE_LABEL;

export const getAnyValueFilterLabel = (column: ReportColumn): string =>
  isReferenceColumn(column) ? HAS_REFERENCE_LABEL : HAS_VALUE_LABEL;

const getAsyncMultiselectFilterInputProps = (
  column: ReportColumn,
  filters: APIReportFilters,
  removeFilterExpression: ReportFilterInputCommands['removeFilterExpression'],
  setFilterExpression: ReportFilterInputCommands['setFilterExpression'],
  loadComponentSuggestions: Maybe<ReportColumnSuggestionsFetchFn>
): ColumnFilterMultiselectWithLoadSuggestionsProps => {
  let selected =
    filters[column.key].values.map(v => ({
      ...v,
      label: isHasNoValueFilterExpression(v)
        ? getNoValueFilterLabel(column)
        : (v.label ?? String(v.value)),
    })) ?? [];

  const hasAnyValueSelected = selected.find(isHasAnyValueFilterExpression);
  selected = hasAnyValueSelected
    ? [
        {
          ...hasAnyValueSelected,
          value: HAS_VALUE_OR_REFERENCE_VALUE,
          label: getAnyValueFilterLabel(column),
        },
      ]
    : selected;

  return {
    column,
    selected,
    loadSuggestions: loadComponentSuggestions!, // todo fix type
    onValueChange: newSelectedValues => {
      setFilterExpression(
        column,
        consolidateFilterValues(newSelectedValues, selected)
      );
    },
    removeFilter: removeFilterExpression,
  };
};

const getTextFilterInputProps = (
  column: ReportColumn,
  filters: APIReportFilters,
  removeFilterExpression: ReportFilterInputCommands['removeFilterExpression'],
  setFilterExpression: ReportFilterInputCommands['setFilterExpression'],
  loadComponentSuggestions: Maybe<ReportColumnSuggestionsFetchFn>
): TextColumnFilterProps => {
  const selected =
    filters[column.key].values.map(v => ({
      ...v,
      label: v.label ?? String(v.value),
    })) ?? [];

  return {
    column,
    selected,
    loadSuggestions: loadComponentSuggestions!, // todo fix type
    removeFilter: removeFilterExpression,
    changeFilterExpression: setFilterExpression,
  };
};

const getCheckboxFilterInputProps = (
  column: ReportColumn,
  filters: APIReportFilters,
  removeFilterExpression: ReportFilterInputCommands['removeFilterExpression'],
  setFilterExpression: ReportFilterInputCommands['setFilterExpression']
): CheckboxColumnFilterProps => {
  const currentColumnFilterValues = filters[column.key]?.values ?? [];

  const hasAnyValueSelected = currentColumnFilterValues.find(
    isHasAnyValueFilterExpression
  );

  const selected = hasAnyValueSelected
    ? [HAS_VALUE_OR_REFERENCE_VALUE]
    : currentColumnFilterValues
        .map(v => v.value)
        .map(CheckboxActualValueToDisplayValue);

  return {
    column,
    selected,
    options: getCheckboxColumnFilterOptions(column),
    onValueChange: newSelectedValues => {
      const finalValues = newSelectedValues
        ? newSelectedValues.map(v => ({
            value: CheckboxDisplayValueToActualValue(v),
          }))
        : [];

      setFilterExpression(
        column,
        consolidateFilterValues(finalValues, currentColumnFilterValues)
      );
    },
    removeFilter: removeFilterExpression,
  };
};

const getValuePresenceFilterInputProps = (
  column: ReportColumn,
  filters: APIReportFilters,
  removeFilterExpression: ReportFilterInputCommands['removeFilterExpression'],
  setFilterExpression: ReportFilterInputCommands['setFilterExpression']
): ValuePresenceColumnFilterProps => {
  const selected = filters[column.key]?.values ?? [];
  return {
    column,
    selected,
    removeFilter: removeFilterExpression,
    changeFilterExpression: setFilterExpression,
  };
};

// unknown filters could caused by removed columns, changed column types.
const getUnknownFilterInputProps = (
  columnKey: string,
  filterValues: FilterExpression[],
  removeFilterExpression: ReportFilterInputCommands['removeFilterExpression'],
  i18nLocale: I18nLocale
): UnknownColumnFilterInputProps => {
  const column: { key: string; label: string; type: null } = {
    key: columnKey,
    label: columnKey,
    type: null,
  };

  const expressionForDecidingOperator = filterValues.find(
    v => !isEmptyOptionSelected(v.operator)
  ) ??
    filterValues[0] ?? { value: null, operator: undefined };

  const isEmptyOrNotEmptyOperatorSelected = isEmptyOptionSelected(
    expressionForDecidingOperator.operator
  );

  const isBetweenOperatorSelected = isBetweenOptionSelected(
    expressionForDecidingOperator.operator
  );

  const toPossibleDisplayString = (v: FilterValue): string | undefined => {
    if (v === undefined) return undefined;
    if (v === null) return UNSET_OR_NOTAPPLICABLE_VALUE_LABEL;
    if (v === true) return 'Checked';
    if (v === false) return 'Unchecked';
    if (v === HAS_VALUE_OR_REFERENCE_VALUE) return HAS_VALUE_LABEL;
    if (typeof v === 'string' && isRelativeDate(v)) {
      return relativeDateDisplayLabel(v, i18nLocale) || v;
    }

    return String(v);
  };

  const toPossibleBetweenDisplayString = (
    v: FilterValue
  ): { start: string; end: string } | undefined => {
    if (v !== null && typeof v === 'object' && 'start' in v && 'end' in v) {
      return {
        start: toPossibleDisplayString(v.start) ?? '',
        end: toPossibleDisplayString(v.end) ?? '',
      };
    }
    return undefined;
  };

  const otherFilterValues = filterValues
    .map(v => toPossibleDisplayString(v.label ?? v.value ?? null))
    .filter(isTruthy);

  const value = isEmptyOrNotEmptyOperatorSelected
    ? undefined
    : isBetweenOperatorSelected
      ? toPossibleBetweenDisplayString(
          expressionForDecidingOperator.value ?? null
        )
      : otherFilterValues;

  return {
    column,
    operator: expressionForDecidingOperator.operator,
    removeFilter: removeFilterExpression,
    value,
  };
};

export const getFiltersToRender = ({
  filters,
  filterableColumns,
  locale,
  i18nLocale,
  removeFilterExpression,
  setFilterExpression,
  loadComponentSuggestions,
  excludedOperators,
}: {
  filters: APIReportFilters | null;
  filterableColumns: ReportColumn[];
  locale: Locale;
  i18nLocale: I18nLocale;
  removeFilterExpression: ReportFilterInputCommands['removeFilterExpression'];
  setFilterExpression: ReportFilterInputCommands['setFilterExpression'];
  loadComponentSuggestions: Maybe<ReportColumnSuggestionsFetchFn>;
  excludedOperators?: Operator[];
}): FilterInputProps[] =>
  filters
    ? Object.keys(filters)
        .map<FilterInputProps | null>(columnKey => {
          const column = filterableColumns
            .flatMap(splitDateRangeColumnForUI)
            .find(col => col.key === columnKey);
          const filterValues = filters[columnKey].values;

          // the column used by the filter might be removed from the report,
          // or the column type might be changed, so the filer value is not valid anymore,
          // in both cases we want to render an unknown filter with error message and allow user to remove it to fix.
          // when adding new filters, might have incomplete filter expressions, we don't want to treat them as invalid
          if (
            !column ||
            (!isFilterIncomplete(filterValues) &&
              !isFilterValid(filterValues, column))
          ) {
            return {
              inputType: FilterInputType.UNKNOWN,
              props: getUnknownFilterInputProps(
                columnKey,
                filterValues,
                removeFilterExpression,
                i18nLocale
              ),
            };
          }

          if (columnPredicates.isNumberColumn(column)) {
            return {
              inputType: FilterInputType.NUMERIC,
              props: getFilterWithOperatorProps(
                column,
                filters,
                locale,
                removeFilterExpression,
                setFilterExpression,
                excludedOperators
              ),
            };
          }

          if (columnPredicates.isDateColumn(column)) {
            return {
              inputType: FilterInputType.DATE,
              props: getFilterWithOperatorProps(
                column,
                filters,
                locale,
                removeFilterExpression,
                setFilterExpression,
                excludedOperators
              ),
            };
          }

          if (columnPredicates.isCheckBoxColumn(column)) {
            return {
              inputType: FilterInputType.CHECKBOX,
              props: getCheckboxFilterInputProps(
                column,
                filters,
                removeFilterExpression,
                setFilterExpression
              ),
            };
          }

          if (columnPredicates.isTextColumn(column)) {
            return {
              inputType: FilterInputType.TEXT,
              props: getTextFilterInputProps(
                column,
                filters,
                removeFilterExpression,
                setFilterExpression,
                loadComponentSuggestions
              ),
            };
          }

          if (columnPredicates.isEmailColumn(column)) {
            return {
              inputType: FilterInputType.EMAIL,
              props: getTextFilterInputProps(
                column,
                filters,
                removeFilterExpression,
                setFilterExpression,
                loadComponentSuggestions
              ),
            };
          }

          if (
            columnPredicates.isUserColumn(column) ||
            columnPredicates.isReferenceColumn(column) ||
            columnPredicates.isListColumn(column) ||
            columnPredicates.isSelectMultipleListColumn(column) ||
            columnPredicates.isParentColumn(column) ||
            columnPredicates.isSourceColumn(column) ||
            columnPredicates.isTargetColumn(column)
          ) {
            return {
              inputType: FilterInputType.ASYNC_MULTISELECT,
              props: getAsyncMultiselectFilterInputProps(
                column,
                filters,
                removeFilterExpression,
                setFilterExpression,
                loadComponentSuggestions
              ),
            };
          }

          return {
            inputType: FilterInputType.VALUE_PRESENCE,
            props: getValuePresenceFilterInputProps(
              column,
              filters,
              removeFilterExpression,
              setFilterExpression
            ),
          };
        })
        .filter(isTruthy)
    : [];

const isHasAnyValueFilterExpression = (
  filterExpression: FilterExpression
): boolean => filterExpression.operator === Operator.IS_NOT_EMPTY;

const isHasNoValueFilterExpression = (
  filterExpression: FilterExpression
): boolean => filterExpression.operator === Operator.IS_EMPTY;

const toConsolidatedFilterExpression = (
  value: FilterValue,
  operator: Operator,
  label?: string
): FilterExpression => {
  const result: FilterExpression = { value, operator };
  if (label !== undefined) {
    result.label = label;
  }
  return result;
};

// currently needed by checkbox or reference columns filters, which has no operator selectors
// to consolidates the filter values and operators
export const consolidateFilterValues = (
  newSelectedValues: Array<{ value: FilterValue; label?: string }> = [],
  formerFilters: FilterExpression[] = []
): FilterExpression[] => {
  const hasAnyValueSelectedOld = formerFilters?.find(
    isHasAnyValueFilterExpression
  );

  const shouldRemoveHasAnyValueFilter =
    hasAnyValueSelectedOld && newSelectedValues.length > 1;

  if (shouldRemoveHasAnyValueFilter) {
    return newSelectedValues
      .filter(v => v.value !== HAS_VALUE_OR_REFERENCE_VALUE)
      .map(v =>
        toConsolidatedFilterExpression(
          v.value,
          isNull(v.value) ? Operator.IS_EMPTY : Operator.EQUAL,
          v.label
        )
      );
  }

  const hasAnyValueSelectedNew = newSelectedValues.find(
    v => v.value === HAS_VALUE_OR_REFERENCE_VALUE
  );
  if (!hasAnyValueSelectedOld && hasAnyValueSelectedNew) {
    return [
      toConsolidatedFilterExpression(
        null,
        Operator.IS_NOT_EMPTY,
        hasAnyValueSelectedNew.label
      ),
    ];
  }

  return newSelectedValues.map(v =>
    toConsolidatedFilterExpression(
      v.value,
      isNull(v.value) ? Operator.IS_EMPTY : Operator.EQUAL,
      v.label
    )
  );
};

export const keepValidFilters = (
  filters: APIReportFilters,
  columns: ReportColumn[]
): APIReportFilters | null => {
  const validFilters = pickBy(filters, ({ values }, columnKey) => {
    const column = columns
      .flatMap(splitDateRangeColumnForUI)
      .find(c => c.key === columnKey);
    return isFilterValid(values, column);
  });

  return isEmpty(validFilters) ? null : validFilters;
};

export const removeExcludedOperators = (
  options: SelectOption<Operator>[],
  excludedOperators?: Operator[]
): SelectOption<Operator>[] =>
  excludedOperators
    ? options.filter(option => !excludedOperators.includes(option.value))
    : options;

export const getActiveBarFilters = (
  filters: APIReportFilters,
  filterableColumns: ReportColumn[]
): ActiveBarFilters => {
  return Object.entries(filters)
    .map(([key, { values }]) => {
      if (isFilterIncomplete(values)) {
        return null;
      }

      const column = filterableColumns
        .flatMap(splitDateRangeColumnForUI)
        .find(column => column.key === key);

      if (!column)
        return {
          value: values.map(valueOption => valueOption.value),
          label: key,
          type: key,
          iconName: IconName.FIELD,
          isInvalid: true,
        };
      const isReference = isReferenceColumn(column);
      const isColumnWithCustomName = isColumnWithCustomLabel(column);

      return {
        value: values.map(valueOption => valueOption.value),
        label: column.label,
        type: key,
        iconName: isReference
          ? IconName.REFERENCE
          : isUserColumn(column)
            ? IconName.PEOPLE
            : IconName.FIELD,
        rightIconName:
          !isReference || isColumnWithCustomName
            ? undefined
            : column.type === ReportColumnType.REFERENCE_TYPE_INCOMING
              ? IconName.ARROW_BACK
              : IconName.ARROW_FORWARD,
        isInvalid: !isFilterValid(values, column),
      };
    })
    .filter(isTruthy)
    .filter(filter => !isEmpty(filter.value));
};
